import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Menu, MenuItem, ListItemText, Typography, Divider, Box } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { api } from '../../api/api';
import { STATUS } from '../../config/platformModules';

// 상단 종 아이콘 — 처리할 일 (승인 대기 가입 등). admin 만, 1분마다 갱신 (N1, 12.01-D011)
const POLL_MS = 60 * 1000;

export function useNotificationSummary(enabled) {
  const [summary, setSummary] = useState({ total: 0, items: [] });
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get('/notifications/summary');
      setSummary(data || { total: 0, items: [] });
    } catch (_) { /* 알림 실패는 화면을 막지 않는다 */ }
  }, [enabled]);
  useEffect(() => {
    load();
    if (!enabled) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load, enabled]);
  return { summary, reload: load };
}

const ago = (d) => {
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return `${m}분 전`;
  if (m < 60 * 24) return `${Math.round(m / 60)}시간 전`;
  return `${Math.round(m / 1440)}일 전`;
};

function NotificationBell({ user, size = 22 }) {
  const isAdmin = user?.role === 'admin';
  const { summary } = useNotificationSummary(isAdmin);
  const [anchor, setAnchor] = useState(null);
  const navigate = useNavigate();

  const icon = <NotificationsIcon sx={{ color: '#551fff', fontSize: size }} />;
  if (!isAdmin) {
    return <div className="header-cluster-bell" title="알림" aria-label="Notifications">{icon}</div>;
  }
  const go = (link) => { setAnchor(null); navigate(link); };
  return (
    <>
      <div
        className="header-cluster-bell"
        role="button"
        tabIndex={0}
        title={summary.total ? `처리할 일 ${summary.total}건` : '알림 없음'}
        aria-label={`Notifications: ${summary.total}`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => { if (e.key === 'Enter') setAnchor(e.currentTarget); }}
      >
        <Badge badgeContent={summary.total} max={99} sx={{ '& .MuiBadge-badge': { bgcolor: STATUS.critical, color: '#fff' } }}>{icon}</Badge>
      </div>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { width: 340, maxWidth: '92vw' } }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>처리할 일</Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.offlineCount ? `TV 오프라인 ${summary.offlineCount}대 · ` : ''}승인 대기 가입 {summary.signupCount ?? summary.total}건
          </Typography>
        </Box>
        <Divider />
        {summary.items.length === 0 && (
          <MenuItem disabled><ListItemText primary="처리할 일이 없습니다" /></MenuItem>
        )}
        {summary.items.map((it) => (
          <MenuItem key={`${it.kind}-${it.id}`} onClick={() => go(it.link)}>
            <ListItemText
              primary={it.kind === 'device_offline' ? `TV 오프라인 — ${it.title}` : `가입 승인 — ${it.title}`}
              secondary={`${it.subtitle} · ${it.kind === 'device_offline' ? '끊긴 지 ' : ''}${ago(it.at)}`}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 600, noWrap: true, color: it.kind === 'device_offline' ? 'error' : undefined }}
              secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
            />
          </MenuItem>
        ))}
        {summary.total > summary.items.length && (
          <MenuItem onClick={() => go('/user')}><ListItemText primary={`전체 보기 (${summary.total})`} primaryTypographyProps={{ fontSize: 13 }} /></MenuItem>
        )}
      </Menu>
    </>
  );
}

export default NotificationBell;
